import { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import "mapbox-gl/dist/mapbox-gl.css";
import { Alert, AlertDescription } from "./alert";
import { AlertCircle } from "lucide-react";
import { Button } from "./button";
import { MapPin } from "lucide-react";

// const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

const MAPBOX_TOKEN =
  "pk.eyJ1IjoiZW1teWR1cnVjIiwiYSI6ImNtYjNtNnNoMDA4cGMyaXNhb3g1Zm14cTIifQ.rYjpHB0JB7V4VO4amNJs-g";

interface SimpleLocationPickerProps {
  onLocationSelect: (latitude: number, longitude: number) => void;
  initialLocation: {
    latitude: number;
    longitude: number;
  };
  onSaveLocation?: () => void;
}

export function SimpleLocationPicker({
  onLocationSelect,
  initialLocation,
  onSaveLocation,
}: SimpleLocationPickerProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const geocoder = useRef<MapboxGeocoder | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentLocation, setCurrentLocation] = useState(initialLocation);

  useEffect(() => {
    if (!MAPBOX_TOKEN) {
      setError(
        "Mapbox token is missing. Please add VITE_MAPBOX_ACCESS_TOKEN to your .env file."
      );
      return;
    }

    if (!mapContainer.current) return;

    try {
      // Initialize map
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/streets-v12",
        center: [initialLocation.longitude, initialLocation.latitude],
        zoom: 13,
        accessToken: MAPBOX_TOKEN,
      });

      // Initialize geocoder
      geocoder.current = new MapboxGeocoder({
        accessToken: MAPBOX_TOKEN,
        mapboxgl: mapboxgl as any,
        marker: false,
        placeholder: "Search for address...",
      });

      // Add geocoder to map
      map.current.addControl(geocoder.current);

      // Initialize marker
      marker.current = new mapboxgl.Marker({
        draggable: true,
        color: "#FF0000",
      })
        .setLngLat([initialLocation.longitude, initialLocation.latitude])
        .addTo(map.current);

      // Handle geocoder result
      geocoder.current.on("result", (e) => {
        const [lng, lat] = e.result.center;
        marker.current?.setLngLat([lng, lat]);
        setCurrentLocation({ latitude: lat, longitude: lng });
        onLocationSelect(lat, lng);
      });

      // Handle marker dragend
      marker.current.on("dragend", () => {
        const lngLat = marker.current?.getLngLat();
        if (lngLat) {
          setCurrentLocation({ latitude: lngLat.lat, longitude: lngLat.lng });
          onLocationSelect(lngLat.lat, lngLat.lng);
        }
      });

      // Cleanup
      return () => {
        map.current?.remove();
      };
    } catch (err) {
      setError(
        "Failed to initialize map. Please check your Mapbox configuration."
      );
    }
  }, []);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <div
        ref={mapContainer}
        className="h-[300px] w-full rounded-md border"
        style={{ position: "relative" }}
      />
      <div className="flex items-center justify-between gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          <span>
            {currentLocation.latitude.toFixed(6)},{" "}
            {currentLocation.longitude.toFixed(6)}
          </span>
        </div>
        {onSaveLocation && (
          <Button onClick={onSaveLocation} size="sm" className="gap-2">
            <MapPin className="h-4 w-4" />
            Save Location
          </Button>
        )}
      </div>
    </div>
  );
}
